import React from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';

const ExerciseCard = ({ exercise, viewMode = 'grid', isFavorite, onToggleFavorite, onSelect }) => {
  const categoryIcons = {
    strength: 'Dumbbell',
    cardio: 'Heart',
    flexibility: 'Sparkles',
    balance: 'Target',
    sports: 'Trophy'
  };

  const difficultyColors = {
    beginner: 'text-green-600 bg-green-100',
    intermediate: 'text-yellow-600 bg-yellow-100',
    advanced: 'text-red-600 bg-red-100'
  };

  if (viewMode === 'list') {
    return (
      <motion.div
        className="bg-card border border-border rounded-xl p-4 hover:shadow-lg transition-shadow cursor-pointer"
        whileHover={{ y: -2 }}
        onClick={onSelect}
      >
        <div className="flex items-center gap-4">
          {/* Icon */}
          <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
            <Icon name={categoryIcons?.[exercise?.category] || 'Dumbbell'} size={24} className="text-primary" />
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2 mb-1">
              <h3 className="font-semibold text-foreground truncate">{exercise?.name}</h3>
              <button
                onClick={(e) => {
                  e?.stopPropagation();
                  onToggleFavorite();
                }}
                className="flex-shrink-0"
              >
                <Icon 
                  name={isFavorite ? 'Star' : 'Star'} 
                  size={18} 
                  className={isFavorite ? 'text-yellow-500 fill-yellow-500' : 'text-muted-foreground'}
                />
              </button>
            </div>
            <p className="text-sm text-muted-foreground line-clamp-1 mb-2">
              {exercise?.description}
            </p>
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`text-xs px-2 py-1 rounded-full ${difficultyColors?.[exercise?.difficulty]}`}>
                {exercise?.difficulty}
              </span>
              {exercise?.caloriesPerMinute && (
                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                  <Icon name="Flame" size={14} />
                  <span>{exercise?.caloriesPerMinute} cal/min</span>
                </div>
              )}
              {exercise?.muscleGroups && exercise?.muscleGroups?.length > 0 && (
                <span className="text-xs text-muted-foreground">
                  {exercise?.muscleGroups?.slice(0, 2)?.join(', ')}
                </span>
              )}
            </div>
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      className="bg-card border border-border rounded-xl p-6 hover:shadow-lg transition-shadow cursor-pointer relative"
      whileHover={{ y: -4 }}
      onClick={onSelect}
    >
      {/* Favorite Button */}
      <button
        onClick={(e) => {
          e?.stopPropagation();
          onToggleFavorite();
        }}
        className="absolute top-4 right-4 z-10"
      >
        <Icon 
          name={isFavorite ? 'Star' : 'Star'} 
          size={20} 
          className={isFavorite ? 'text-yellow-500 fill-yellow-500' : 'text-muted-foreground'}
        />
      </button>
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
            <Icon name={categoryIcons?.[exercise?.category] || 'Dumbbell'} size={24} className="text-primary" />
          </div>
          <div>
            <h3 className="font-semibold text-foreground">{exercise?.name}</h3>
            <span className={`text-xs px-2 py-1 rounded-full ${difficultyColors?.[exercise?.difficulty]}`}>
              {exercise?.difficulty}
            </span>
          </div>
        </div>
      </div>
      <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
        {exercise?.description}
      </p>
      <div className="space-y-2">
        {exercise?.muscleGroups && exercise?.muscleGroups?.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {exercise?.muscleGroups?.slice(0, 3)?.map((muscle, idx) => (
              <span key={idx} className="text-xs px-2 py-1 bg-muted rounded-full text-muted-foreground">
                {muscle}
              </span>
            ))}
          </div>
        )}

        {exercise?.equipmentNeeded && exercise?.equipmentNeeded?.length > 0 && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Icon name="Package" size={16} />
            <span>{exercise?.equipmentNeeded?.join(', ')}</span>
          </div>
        )}

        {exercise?.caloriesPerMinute && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Icon name="Flame" size={16} />
            <span>{exercise?.caloriesPerMinute} cal/min</span>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default ExerciseCard;